import {ReactNode, createContext, useCallback, useContext, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router';

import {brandT} from '@/lib/types/brand.types';
import {marketT} from '@/lib/types/market.types';
import {retailerT} from '@/lib/types/retailer.types';

import {defaultCreateData} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useItemQuery} from '@/hooks/useItemQuery';

import {useAuth} from '@/providers/components/authProvider';
import {useRetailers} from '@/providers/components/retailersProvider';

/*******************************************************************
 * RetailerDetailsContext
 *******************************************************************/
type RetailerDetailsContextT = {
  // Properties
  retailer: retailerT | undefined;
  isLoading: boolean;
  error: Error | null;
  distributorId: number;
  // Functions
  refreshRetailer: () => Promise<void>;
  updateRetailer: (retailer: retailerT) => Promise<retailerT>;
  deleteRetailer: (id: number) => Promise<void>;
  searchBrandsToAdd: (name: string) => Promise<brandT[]>;
  addBrandToRetailer: (id: number) => Promise<void>;
  removeBrandFromRetailer: (id: number) => Promise<void>;
  searchMarketsToAdd: (name: string) => Promise<marketT[]>;
  addRetailerToMarket: (market_id: number) => Promise<void>;
  removeRetailerFromMarket: (market_id: number) => Promise<void>;
};

const RetailerDetailsContext = createContext({} as RetailerDetailsContextT);

/*******************************************************************
 * RetailerDetailsProvider
 *******************************************************************/
type RetailerDetailsProviderPropsT = {
  children: ReactNode;
  id?: number;
};

/**
 * Provides the retailer object to the children components and useRetailer custom hook. Gets the retailer
 * by ID from the database and navigates back to the retailer list if there's an error. The ID for the
 * retailer is plucked from the URL params. If there's no valid ID, the component will navigate back to the
 * retailer list.
 *
 * **NOTE** - This component is designed to be controlled via the URL params. There is no way to set the
 * retailer that is being fetched manually. Example: /admin/retailers/:retailerId - so if the retailerId is
 * not present in the URL, the component will navigate back to the retailer list.
 *
 * @param {RetailerDetailsProviderPropsT} props - The component props.
 * @returns {Context<RetailerDetailsContextT>} - The retailer provider component.
 */
export const RetailerDetailsProvider = ({children}: RetailerDetailsProviderPropsT) => {
  const navigate = useNavigate();
  const {isAdministrator} = useAuth();
  const {updateData: updateRetailerMutation, deleteData: deleteRetailerMutation} = useRetailers();
  const {id: retailerIdFromParams} = useParams();

  // Constants
  const retailerId = Number(retailerIdFromParams);

  // TanStack Query hooks
  const {
    data: fetchedRetailer,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useItemQuery({
    // Specify the table
    table: 'Retailer',
    // Specify the ID of the retailer to fetch
    id: retailerId,
    // Optional - specify the columns to fetch
    // columns: ['*'], // default
    enabled: !!retailerId,
  });

  // Functions
  /*
   * Gets a retailer records from the database.
   */
  const getRetailer = useCallback(async () => {
    if (!isAdministrator) throw new Error('Error getting retailers');
    try {
      const {data} = await refetch();
      const _retailer = data ?? null;
      return _retailer;
    } catch (error) {
      console.error('Error in getRetailer:', error);
      return undefined;
    }
  }, [isAdministrator, refetch]);

  /*
   * Refreshes the retailer data from the database.
   */
  const refreshRetailer = async () => {
    await getRetailer();
  };

  /*
   * Updates the retailer in the database.
   */
  const updateRetailer = async (retailer: retailerT) => {
    if (!isAdministrator) throw new Error('Error updating retailer');

    try {
      const updatedRetailer = await updateRetailerMutation(retailer);
      return updatedRetailer;
    } catch (error) {
      console.error('Error in updateRetailer:', error);
      throw error;
    }
  };

  /*
   * Deletes the retailer from the database.
   */
  const deleteRetailer = async (id: number) => {
    if (!isAdministrator) throw new Error('Error deleting retailer');

    try {
      await deleteRetailerMutation(id);
      navigate('/admin/retailers');
    } catch (error) {
      console.error('Error in deleteRetailer:', error);
      throw error;
    }
  };

  /*
   * Searches for brands to add to the retailer
   */
  const searchBrandsToAdd = async (name: string) => {
    try {
      const query = SupabaseInstance.supabase.from('Brand').select('*').ilike('brand_name', `%${name}%`);

      const {data: brands, error: brandError} = await query;

      if (brandError) {
        console.error('Error in searchBrandsToAdd:', brandError);
        throw new Error(brandError.message);
      }

      return brands;
    } catch (error) {
      console.error('Error in searchBrandsToAdd:', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Add a brand to the retailer.
   */
  const addBrandToRetailer = async (brandId: number) => {
    try {
      const createData = await defaultCreateData();

      const data = {
        ...createData,
        fk_retailer_id: retailerId,
        fk_brand_id: brandId,
        date_removed: null,
        removed_by: null,
      };

      const {error} = await SupabaseInstance.supabase.from('Retailer_Brand').upsert(data, {
        onConflict: 'fk_retailer_id,fk_brand_id',
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in addBrandToRetailer:', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a brand from the retailer
   */
  const removeBrandFromRetailer = async (brandId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Retailer_Brand')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_brand_id', brandId)
        .eq('fk_retailer_id', retailerId);

      if (error) throw new Error(error.message);
    } catch (error) {
      console.error('Error in removeBrandFromRetailer:', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Adds a retailer to the market
   */
  const addRetailerToMarket = async (marketId: number) => {
    try {
      const createData = await defaultCreateData();

      const data = {
        ...createData,
        fk_retailer_id: retailerId,
        fk_market_id: marketId,
        date_removed: null,
        removed_by: null,
      };

      const {error} = await SupabaseInstance.supabase
        .from('Market_Retailer')
        .upsert(data, {
          onConflict: 'fk_retailer_id,fk_market_id',
        })
        .select()
        .single();

      if (error) {
        console.error('error in add retailer to market', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('error in add retailer to market', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a retailer from the market
   */
  const removeRetailerFromMarket = async (marketId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Market_Retailer')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_retailer_id', retailerId)
        .eq('fk_market_id', marketId);

      if (error) throw new Error(error?.message || 'Error removing retailer from market');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Searches for markets to add to the retailer to
   */
  const searchMarketsToAdd = async (name: string) => {
    try {
      const query = SupabaseInstance.supabase.from('Market').select('*').ilike('market_name', `%${name}%`);

      const {data: markets, error: marketError} = await query;

      if (marketError) {
        console.error('Error in searchMarketsToAdd:', marketError);
        throw new Error(marketError.message);
      }

      return markets;
    } catch (error) {
      console.error('Error in searchMarketsToAdd:', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Configuration
   */
  useEffect(() => {
    // If there's an error getting the retailer, navigate back to the retailer list
    if (error) {
      console.error(error);
      navigate('/admin/retailers');
    }
  }, [error, navigate]);

  return (
    <RetailerDetailsContext.Provider
      value={{
        retailer: fetchedRetailer,
        distributorId: retailerId,
        isLoading: isLoading || isFetching,
        error,
        refreshRetailer,
        updateRetailer,
        deleteRetailer,
        searchBrandsToAdd,
        addBrandToRetailer,
        removeBrandFromRetailer,
        searchMarketsToAdd,
        addRetailerToMarket,
        removeRetailerFromMarket,
      }}
    >
      {children}
    </RetailerDetailsContext.Provider>
  );
};

/*******************************************************************
 * useRetailer Hook
 *******************************************************************/

export const useRetailer = () => {
  const context = useContext(RetailerDetailsContext);

  // Make sure the context is not used outside of the RetailerDetailsProvider
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype)
    throw new Error('useRetailer must be used within a RetailerProvider');

  return context;
};
